import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AsistenciaResponse, EstadoAsistencia } from 'types/asistencia.types';
import { asistenciaService } from 'services/asistenciaService';
import { matriculaService } from 'services/matriculaService';
import { anioEscolarService } from 'services/usuarioService';
import { AñoEscolar } from 'types/usuario.types';
import { CursoEstudianteItem } from 'types/matricula.types';

// ============================================================
// useMiAsistencia – asistencia del estudiante por año/asignación
// ============================================================

export interface ResumenAsistencia {
  total:       number;
  presente:    number;
  ausente:     number;
  tardanza:    number;
  justificado: number;
  porcentaje:  number;
}

export const calcularResumen = (registros: AsistenciaResponse[]): ResumenAsistencia => {
  const total       = registros.length;
  const presente    = registros.filter(r => r.estado === 'PRESENTE').length;
  const ausente     = registros.filter(r => r.estado === 'AUSENTE').length;
  const tardanza    = registros.filter(r => r.estado === 'TARDANZA').length;
  const justificado = registros.filter(r => r.estado === 'JUSTIFICADO').length;
  const porcentaje  = total > 0 ? Math.round(((presente + tardanza + justificado) / total) * 100) : 0;
  return { total, presente, ausente, tardanza, justificado, porcentaje };
};

export const useMiAsistencia = () => {
  const { user } = useAuth();

  // Catálogos
  const [anios, setAnios]   = useState<AñoEscolar[]>([]);
  const [cursos, setCursos] = useState<CursoEstudianteItem[]>([]);

  // Selección
  const [anioId, setAnioId]         = useState<number | null>(null);
  const [asignacionId, setAsignacionId] = useState<number | null>(null);

  // Datos
  const [registros, setRegistros] = useState<AsistenciaResponse[]>([]);

  // Estados de carga
  const [loadingAnios, setLoadingAnios]   = useState(true);
  const [loadingCursos, setLoadingCursos] = useState(false);
  const [loadingData, setLoadingData]     = useState(false);
  const [error, setError]                 = useState<string | null>(null);

  // Filtro de estado (sólo para la vista de tabla)
  const [filtroEstado, setFiltroEstado] = useState<EstadoAsistencia | ''>('');

  // ── 1. Cargar años escolares al montar ───────────────────────
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoadingAnios(true);
      setError(null);
      try {
        const data = await anioEscolarService.listarTodos();
        if (cancelled) return;
        const activos = data.filter(a => a.activo);
        setAnios(activos);
        const primero = activos[0] ?? null;
        if (primero) setAnioId(primero.idAnioEscolar);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Error al cargar años escolares');
      } finally {
        if (!cancelled) setLoadingAnios(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── 2. Cargar cursos cuando cambia el año ────────────────────
  useEffect(() => {
    if (!user || anioId === null) return;
    let cancelled = false;
    setCursos([]);
    setAsignacionId(null);
    setRegistros([]);
    setLoadingCursos(true);
    setError(null);
    (async () => {
      try {
        const resp = await matriculaService.getCursosByEstudiante(user.idUsuario, anioId);
        if (cancelled) return;
        setCursos(resp.cursos ?? []);
        // Seleccionar primer curso automáticamente
        const primero = resp.cursos?.[0] ?? null;
        if (primero) setAsignacionId(primero.asignacionAcademicaId);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Error al cargar cursos');
      } finally {
        if (!cancelled) setLoadingCursos(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, anioId]);

  // ── 3. Cargar asistencia cuando cambia asignación ────────────
  const cargarAsistencia = useCallback(async () => {
    if (!user || anioId === null || asignacionId === null) return;
    let cancelled = false;
    setLoadingData(true);
    setError(null);
    setRegistros([]);
    try {
      const data = await asistenciaService.listarPorEstudianteAnioAsignacion(
        user.idUsuario,
        anioId,
        asignacionId,
      );
      if (!cancelled) setRegistros(data);
    } catch (e: any) {
      if (!cancelled) setError(e?.message ?? 'Error al cargar asistencias');
    } finally {
      if (!cancelled) setLoadingData(false);
    }
    return () => { cancelled = true; };
  }, [user, anioId, asignacionId]);

  useEffect(() => {
    cargarAsistencia();
  }, [cargarAsistencia]);

  // Registros filtrados por estado
  const registrosFiltrados = filtroEstado
    ? registros.filter(r => r.estado === filtroEstado)
    : registros;

  const resumen         = calcularResumen(registros);
  const resumenFiltrado = calcularResumen(registrosFiltrados);

  const loading = loadingAnios || loadingCursos || loadingData;

  return {
    // catálogos
    anios,
    cursos,
    // selección
    anioId,       setAnioId,
    asignacionId, setAsignacionId,
    // datos
    registros: registrosFiltrados,
    resumen,
    resumenFiltrado,
    // estados
    loading,
    loadingAnios,
    loadingCursos,
    loadingData,
    error,
    // filtro
    filtroEstado, setFiltroEstado,
  };
};
